import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { formatRoas } from "@/lib/signal";
import SignalBadge from "@/components/SignalBadge";
import { LineChart, Line, ResponsiveContainer, YAxis, XAxis, Tooltip, CartesianGrid } from "recharts";
import { Lock, Sparkles, Trophy, TrendingDown, Loader2 } from "lucide-react";

export default function Insights() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [insight, setInsight] = useState(null);
  const [logs, setLogs] = useState([]);
  const [genLoading, setGenLoading] = useState(false);

  useEffect(() => {
    api.get("/logs").then((r) => setLogs(r.data || [])).catch((err) => { if (process.env.NODE_ENV !== "production") console.error("logs fetch failed", err); });
    if (user?.has_access) {
      api.get("/insights/stats").then((r) => setStats(r.data)).catch((err) => { if (process.env.NODE_ENV !== "production") console.error("insights/stats failed", err); });
      api.get("/insights/latest").then((r) => setInsight(r.data)).catch((err) => { if (process.env.NODE_ENV !== "production") console.error("insights/latest failed", err); });
    }
  }, [user?.has_access]);

  const generate = async () => {
    setGenLoading(true);
    try {
      const r = await api.post("/insights/generate");
      setInsight({ text: r.data.insight, created_at: new Date().toISOString() });
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.error("insights/generate failed", err);
    } finally {
      setGenLoading(false);
    }
  };

  if (!user?.has_access) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <main className="max-w-4xl mx-auto px-4 sm:px-6 py-10">
          <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">Insights</h1>
          <p className="mt-2 text-slate-600">Best/worst weeks, scaling confidence, and an AI-written action plan.</p>

          <Card className="mt-8 p-10 rounded-2xl bg-white border-slate-200 relative overflow-hidden text-center">
            <div className="absolute inset-0 pointer-events-none opacity-50 bg-[radial-gradient(ellipse_at_top,rgba(15,23,42,0.04),transparent_50%)]" />
            <div className="relative">
              <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-100 text-slate-600 text-xs font-bold tracking-widest uppercase mb-4">
                <Lock className="h-3 w-3" /> Pro feature
              </span>
              <h2 className="font-display font-bold text-3xl text-slate-900">Unlock weekly AI insight</h2>
              <p className="mt-3 text-slate-600 max-w-lg mx-auto">
                Claude reads your last 6 weeks and writes a 3-sentence action plan. Plus full trend chart, best/worst weeks, and scaling confidence score.
              </p>
              <Link to="/upgrade" className="inline-block mt-6" data-testid="insights-upgrade-cta">
                <Button className="bg-slate-900 hover:bg-slate-800 h-12 px-6">
                  <Sparkles className="mr-2 h-4 w-4" /> Upgrade to Pro
                </Button>
              </Link>
            </div>
          </Card>

          <Card className="mt-6 p-6 rounded-2xl bg-white border-slate-200">
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400">Preview</p>
            <div className="mt-3 grid grid-cols-3 gap-3 filter blur-sm pointer-events-none">
              <Mini label="Best ROAS" value="4.10" />
              <Mini label="Worst ROAS" value="1.20" />
              <Mini label="Confidence" value="72%" />
            </div>
          </Card>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <main className="max-w-5xl mx-auto px-4 sm:px-6 py-10" data-testid="insights-page">
        <h1 className="font-display font-bold text-3xl sm:text-4xl tracking-tight text-slate-900">Insights</h1>
        <p className="mt-2 text-slate-600">Performance over time and your weekly AI insight.</p>

        {/* AI Insight card */}
        <Card className="mt-8 p-6 sm:p-8 rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 text-white border-none">
          <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              <span className="font-display font-semibold">AI Weekly Insight</span>
            </div>
            <Button onClick={generate} disabled={genLoading} className="bg-white text-slate-900 hover:bg-slate-100 h-9" data-testid="generate-insight-button">
              {genLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Regenerate"}
            </Button>
          </div>
          <p className="text-base sm:text-lg leading-relaxed" data-testid="insight-text">
            {insight?.text || "Click Regenerate to get this week's tactical insight from Claude."}
          </p>
        </Card>

        {/* Stats grid */}
        {stats && stats.count > 0 && (
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <Mini label="Avg ROAS" value={formatRoas(stats.avg_roas)} />
            <Mini label="Avg Buffer" value={(stats.avg_buffer >= 0 ? "+" : "") + formatRoas(stats.avg_buffer)} accent={stats.avg_buffer >= 0 ? "text-emerald-600" : "text-red-600"} />
            <Mini label="Best week" value={formatRoas(stats.best.roas)} icon={<Trophy className="h-4 w-4 text-amber-500" />} sub={stats.best.week_label} />
            <Mini label="Worst week" value={formatRoas(stats.worst.roas)} icon={<TrendingDown className="h-4 w-4 text-red-500" />} sub={stats.worst.week_label} />
          </div>
        )}

        {/* Confidence */}
        {stats && (
          <Card className="mt-6 p-6 rounded-2xl bg-white border-slate-200">
            <div className="flex items-baseline justify-between">
              <h3 className="font-display font-semibold text-lg text-slate-900">Scaling confidence</h3>
              <span className="font-display font-bold text-3xl text-slate-900" data-testid="confidence-score">{stats.confidence}%</span>
            </div>
            <div className="mt-3 h-2 rounded-full bg-slate-100 overflow-hidden">
              <div className="h-full bg-emerald-500" style={{ width: `${stats.confidence}%` }} />
            </div>
            <p className="text-sm text-slate-500 mt-3">Based on positive-buffer ratio and frequency of Scale signals across {stats.count} weeks.</p>
          </Card>
        )}

        {/* Advice Adherence */}
        {stats?.adherence && (
          <Card className="mt-6 p-6 rounded-2xl bg-white border-slate-200" data-testid="adherence-card">
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <h3 className="font-display font-semibold text-lg text-slate-900">Advice adherence</h3>
              {stats.adherence.score !== null && stats.adherence.score !== undefined ? (
                <span className="font-display font-bold text-3xl text-slate-900" data-testid="adherence-score">
                  {stats.adherence.score}%
                </span>
              ) : (
                <span className="text-sm text-slate-400">Log a 2nd week to measure</span>
              )}
            </div>
            {stats.adherence.measured_count > 0 ? (
              <>
                <div className="mt-3 flex flex-wrap gap-2">
                  <Tally label="Followed" count={stats.adherence.followed} color="text-emerald-700" bg="bg-emerald-50 border-emerald-200" />
                  <Tally label="Partial" count={stats.adherence.partial} color="text-amber-700" bg="bg-amber-50 border-amber-200" />
                  <Tally label="Ignored" count={stats.adherence.ignored} color="text-slate-600" bg="bg-slate-50 border-slate-200" />
                </div>
                <p className="text-sm text-slate-500 mt-4">How closely you've stuck to your weekly action plan across {stats.adherence.measured_count} measured week{stats.adherence.measured_count === 1 ? "" : "s"}.</p>
                {stats.adherence.recent?.length > 0 && (
                  <div className="mt-5 border-t border-slate-100 pt-4">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Recent</p>
                    <ul className="space-y-2">
                      {stats.adherence.recent.map((r) => (
                        <li key={`${r.week_label}-${r.plan_id ?? "none"}`} className="flex items-start justify-between gap-3 text-sm">
                          <div className="min-w-0">
                            <p className="text-slate-900 font-medium truncate">{r.plan_headline}</p>
                            <p className="text-xs text-slate-500">{r.week_label} · spend delta {r.next_week_delta_pct >= 0 ? "+" : ""}{r.next_week_delta_pct}%</p>
                          </div>
                          <span
                            className={`flex-shrink-0 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-widest border ${
                              r.adherence_status === "followed"
                                ? "bg-emerald-100 text-emerald-700 border-emerald-200"
                                : r.adherence_status === "partial"
                                ? "bg-amber-100 text-amber-700 border-amber-200"
                                : "bg-slate-100 text-slate-600 border-slate-200"
                            }`}
                          >
                            {r.adherence_status}
                          </span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500 mt-3">Log next week to start measuring how well you stick to your action plan.</p>
            )}
          </Card>
        )}

        {/* Full chart */}
        <Card className="mt-6 p-6 rounded-2xl bg-white border-slate-200">
          <h3 className="font-display font-semibold text-lg text-slate-900 mb-3">Full ROAS trend</h3>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={logs.map((l, i) => ({ idx: i + 1, roas: l.roas, be: l.break_even_roas }))} margin={{ top: 8, right: 8, left: -16, bottom: 0 }}>
                <CartesianGrid stroke="#f1f5f9" strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="idx" stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <YAxis stroke="#94a3b8" fontSize={12} tickLine={false} axisLine={false} />
                <Tooltip contentStyle={{ borderRadius: 8, borderColor: "#e2e8f0", fontSize: 12 }} />
                <Line type="monotone" dataKey="be" stroke="#94a3b8" strokeDasharray="4 4" dot={false} />
                <Line type="monotone" dataKey="roas" stroke="#0f172a" strokeWidth={2.5} dot={{ r: 4, fill: "#0f172a" }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </main>
    </div>
  );
}

const Mini = ({ label, value, accent = "text-slate-900", icon, sub }) => (
  <Card className="lift p-5 rounded-2xl bg-white border-slate-200">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{label}</p>
    <div className="mt-1 flex items-center gap-2">
      {icon}
      <p className={`font-display font-bold text-2xl ${accent}`}>{value}</p>
    </div>
    {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
  </Card>
);

const Tally = ({ label, count, color, bg }) => (
  <span className={`inline-flex items-baseline gap-2 px-3 py-1.5 rounded-full border text-sm ${bg}`}>
    <span className={`font-display font-bold ${color}`}>{count}</span>
    <span className={`text-xs uppercase tracking-widest font-bold ${color}`}>{label}</span>
  </span>
);
